<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Container,
		Text,
		AdvancedCard,
		Loader,
		Button,
		toast,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import {
		get_gen_2_component_channel_structure,
		isShutter,
		macToID,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { navigateToDevice, navigateToSetting } from 'src/stores/ui/navigate-to';
	import {
		getComponentByDevice,
		getVirtualComponentsInterface,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import {
		bthomeObjectTitle,
		getBthomedeviceComponents,
		getBthomeDeviceModel,
	} from 'src/helpers/virtual_helpers/virtual_bthome_helper';
	import {
		buildFallbackPairedDevices,
		cloneConfig,
		cloneInputsArray,
		cloneValue,
		countMappings,
		getChannelLabel,
		getFallbackRemoteLabel,
		hasConfigEntries,
	} from 'src/helpers/bthome_remote_controls_helper';
	import BTHomeLearning from './BTHomeLearning.svelte';
	import BTHomeCreateRemoteModal from './BTHomeCreateRemoteModal.svelte';
	import type {
		ComponentsGroupInterface,
		VirtualComponentInterface,
	} from 'src/types/VirtualComponents';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { OutputSelection } from 'src/types/BTHomeTypes';
	import { onMount, onDestroy } from 'svelte';
	import { BtHomeController } from 'src/helpers/virtual_helpers/ble_control_helpers';
	import { btHomeDiscoveredDevices } from 'src/stores/features/ble/bt-home-discovered';
	import { btHomeRemoteIcon } from 'src/stores/features/ble/bt-home-remote-icon';
	import { capFirstChar } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { compare } from 'compare-versions';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { isFwAfter200 } from 'src/helpers/ble_devices_helpers';

	export let device: Device;

	/**
	 * Set of BTHome object IDs that represent remote event sources
	 * (button, dimmer, rotation, channel key events).
	 */
	const REMOTE_EVENT_OBJ_IDS = new Set([58, 60, 63, 96]);
	const mainDeviceCategory = get_gen_2_component_channel_structure(device);

	const getDeviceChannelIndex = (): number | null => {
		if (typeof device?.channel !== 'number') {
			return null;
		}
		if (device.channel >= 0 && device.channel < 100) {
			return device.channel;
		}
		return null;
	};

	let groups: ComponentsGroupInterface[] = [];
	let loading = false;
	let loadingDelete: Record<number, boolean> = {};
	let loadingDevices = true;
	let pairedDevices: string[] = [];
	let mappedRemoteIds: string[] = [];
	let pairedDeviceDetails: Array<Record<string, any>> = [];
	let pairedDeviceLabels: Record<string, string> = {};
	let pairedDeviceChannelLabels: Record<string, Record<string, string>> = {};
	let remoteAddrById: Record<string, string> = {};
	let controlIdByAddr: Record<string, string> = {};
	let mappingCountById: Record<string, number> = {};
	let mappingCountByIdAndChannel: Record<string, Record<number, number>> = {};
	let currentControlInputs: Array<Record<string, any>> = [];
	let remoteInputConfigsById: Record<string, Record<string, any>> = {};
	let remoteOutputById: Record<string, string> = {};
	let defaultOutput: OutputSelection = { id: '0', name: mainDeviceCategory };
	let enumerateSupported: boolean | undefined;
	let enumerateInProgress = false;
	let enumerateResultCache: Record<string, number> | null = null;
	type GetPairedDevicesOptions = {
		forceRefresh?: boolean;
		skipIfHasData?: boolean;
	};

	let getPairedDevicesInProgress = false;
	let getPairedDevicesAttempts = 0;
	let getPairedDevicesBlocked = false;
	let listError: string | null = null;
	let nullResultRetryAttempted = false;
	const MAX_GET_PAIRED_DEVICES_ATTEMPTS = 2;

	let selectedExistingRemote: {
		output: {
			id: string;
			name: string;
		};
		remoteId: string;
		config: Record<string, any>;
		channelLabels: Record<string, string>;
		existingInputs: Array<Record<string, any>>;
		remoteExistsInSettings: boolean;
		hasMappedRemotes: boolean;
		deviceType?: string;
	} | null = null;
	let showCreateRemoteModal = false;

	const ensuredRemoteIds = new Set<string>();

	const deviceID = real_id(device.id);
	$: settingsStore = getDeviceSettings(deviceID);
	$: settings = $settingsStore;
	const fwVersion = (
		settings?.DeviceInfo?.ver ||
		settings?.sys?.device?.fw_id ||
		settings?.DeviceInfo?.fw_id
	)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0];

	const init = (device: Device) => {
		const status: DeviceStatusG2 = readDeviceStatus(deviceID);
		const setting: DeviceSettingsG2 = readDeviceSettings(deviceID);
		if (!setting || !status) {
			return;
		}

		const _groups = [];
		const virtualComponents = getVirtualComponentsInterface(deviceID);
		const trv = Object.keys(setting).filter((i) => /^(blutrv):2\d{2}$/.test(i));
		const controlByAddr = new Map<string, string>();

		Object.entries(setting).forEach(([key, config]) => {
			if (/^bthomecontrol:2\d{2}$/.test(key) && (config as Record<string, any>)?.addr) {
				controlByAddr.set((config as Record<string, any>).addr, key);
			}
		});

		const addedGroupKeys = new Set<string>();

		for (const component in status) {
			if (!setting[component]) {
				continue;
			}

			if (!/^(bthomedevice):2\d{2}$/.test(component)) {
				continue;
			}

			if (
				(device.channel >= 200 && component !== getComponentByDevice(device)) ||
				trv.find((i) => setting[i]?.trv === component)
			) {
				continue;
			}

			const componentConfig = setting[component] as Record<string, any>;
			const controlKey = componentConfig?.addr
				? controlByAddr.get(componentConfig.addr)
				: undefined;
			const groupKey = controlKey || component;

			if (addedGroupKeys.has(groupKey)) {
				continue;
			}

			const groupConfig =
				controlKey && setting[controlKey] && Object.keys(setting[controlKey] || {}).length
					? setting[controlKey]
					: setting[component];
			const groupStatus =
				controlKey && status[controlKey] && Object.keys(status[controlKey] || {}).length
					? status[controlKey]
					: status[component];

			const group: ComponentsGroupInterface = {
				id: groupConfig?.id,
				key: groupKey,
				config: groupConfig,
				status: groupStatus,
				items: [],
			};

			if (group.config?.addr) {
				const items = virtualComponents.filter((el) => el.config?.addr === group.config?.addr);
				group.items = items;
				getBthomedeviceComponents(group, group);
			}

			_groups.push(group);
			addedGroupKeys.add(groupKey);
		}

		groups = _groups;
	};

	let supportsBTHomeRemoteControls = false;
	if (isShutter(device.type)) {
		supportsBTHomeRemoteControls = true;
	} else if (fwVersion) {
		supportsBTHomeRemoteControls = compare(fwVersion, '1.8.0', '>=');
	}

	$: init(device);

	$: clearedDevices = $btHomeDiscoveredDevices?.[deviceID]?.clearedDevices || false;

	$: btEnable =
		isFwAfter200(devices_settings.getByRealId(device.id)) ||
		devices_settings.getByRealId(device.id)?.ble?.enable;

	/**
	 * Gets the component ID for a given output using enumerate, with caching and guards to prevent spam.
	 *
	 * @param {string} output - Output component name (e.g., "rgbcct:0")
	 * @returns {Promise<number>} Component ID, defaults to 200 if enumerate fails or is not supported
	 */
	const getComponentIdFromEnumerate = async (output: string): Promise<number> => {
		if (enumerateSupported === false) {
			return 200;
		}

		if (enumerateResultCache && enumerateResultCache[output] !== undefined) {
			return enumerateResultCache[output];
		}

		if (enumerateInProgress) {
			while (enumerateInProgress) {
				await new Promise((resolve) => setTimeout(resolve, 50));
			}
			if (enumerateResultCache && enumerateResultCache[output] !== undefined) {
				return enumerateResultCache[output];
			}
			return 200;
		}

		try {
			enumerateInProgress = true;
			enumerateSupported = true;

			const enumerateRes: SettingRequest = await BtHomeController.getInstance().enumerate(device);

			if (enumerateRes?.isok && enumerateRes?.result) {
				enumerateResultCache = enumerateRes.result as Record<string, number>;
				const componentId = enumerateResultCache[output];
				if (componentId !== undefined && Number.isFinite(componentId)) {
					return componentId;
				}
				const firstOutput = Object.keys(enumerateResultCache)[0];
				if (firstOutput && enumerateResultCache[firstOutput] !== undefined) {
					return enumerateResultCache[firstOutput];
				}
			}

			enumerateSupported = false;
			return 200;
		} catch (error) {
			enumerateSupported = false;
			console.error('[BTHome] Error enumerating device:', error);
			return 200;
		} finally {
			enumerateInProgress = false;
		}
	};

	/**
	 * Checks if an error is permanent and should not be retried.
	 * Permanent errors include: component not found, invalid arguments, etc.
	 */
	const isPermanentError = (res: SettingRequest | Error | unknown): boolean => {
		if (!res || typeof res !== 'object') {
			return false;
		}
		const r = res as Record<string, any>;
		const errorCode =
			r?.response?.error?.device_error?.code ||
			r?.error?.device_error?.code ||
			r?.data?.error?.device_error?.code;
		const errorMessage =
			r?.response?.error?.device_error?.message ||
			r?.response?.error?.message ||
			r?.error?.device_error?.message ||
			r?.error?.message ||
			r?.errors?.message ||
			r?.data?.error?.message ||
			(typeof r?.error === 'string' ? r.error : null) ||
			'';

		if (errorCode === -105) {
			return true;
		}

		if (typeof errorMessage === 'string') {
			const lowerMessage = errorMessage.toLowerCase();
			if (
				lowerMessage.includes('not found') ||
				lowerMessage.includes('invalid argument') ||
				lowerMessage.includes('does not exist')
			) {
				return true;
			}
		}

		return false;
	};

	/**
	 * Extracts error message from various response error formats.
	 */
	const extractErrorMessage = (res: SettingRequest | Error | unknown): string => {
		if (res instanceof Error) {
			return res.message;
		}
		if (typeof res === 'string') {
			return res;
		}
		if (res && typeof res === 'object') {
			const r = res as Record<string, any>;
			return (
				r?.response?.error?.device_error?.message ||
				r?.response?.error?.message ||
				r?.error?.device_error?.message ||
				r?.error?.message ||
				r?.errors?.message ||
				r?.data?.error?.message ||
				(typeof r?.error === 'string' ? r.error : null) ||
				(typeof r?.errors === 'string' ? r.errors : null) ||
				'Unknown error'
			);
		}
		return 'Unknown error';
	};

	/**
	 * Fetches paired devices from the device.
	 * Handles retries, error display, and state management.
	 */
	const getPairedDevices = async (device: Device, options: GetPairedDevicesOptions = {}) => {
		if (!supportsBTHomeRemoteControls) {
			return;
		}

		const { forceRefresh = false, skipIfHasData = true } = options;

		if (forceRefresh) {
			getPairedDevicesAttempts = 0;
			getPairedDevicesBlocked = false;
			nullResultRetryAttempted = false;
		}

		if (
			skipIfHasData &&
			!forceRefresh &&
			pairedDevices.length > 0 &&
			currentControlInputs.length > 0
		) {
			return;
		}

		if (getPairedDevicesBlocked) {
			return;
		}

		if (getPairedDevicesInProgress) {
			return;
		}

		getPairedDevicesAttempts += 1;
		if (getPairedDevicesAttempts > MAX_GET_PAIRED_DEVICES_ATTEMPTS) {
			getPairedDevicesBlocked = true;
			return;
		}

		getPairedDevicesInProgress = true;
		loadingDevices = true;
		btHomeRemoteIcon.setState(deviceID, false);
		listError = null;

		try {
			const listId =
				enumerateSupported === false
					? 200
					: await getComponentIdFromEnumerate(mainDeviceCategory);

			const res: SettingRequest = await BtHomeController.getInstance().list(device, listId);

			if (res?.isok && Array.isArray(res?.data?.inputs) && res.data.inputs.length > 0) {
				getPairedDevicesAttempts = 0;
				getPairedDevicesBlocked = false;
				nullResultRetryAttempted = false;
				listError = null;
				defaultOutput = parseOutputSelection(res.data?.output);
				currentControlInputs = cloneInputsArray(res.data.inputs || []);
				applyPairedDevices(res.data.inputs || [], res.data?.output);
				return;
			}

			if (
				res?.isok &&
				(res?.data === null || res?.data === undefined || !Array.isArray(res?.data?.inputs))
			) {
				if (!nullResultRetryAttempted) {
					nullResultRetryAttempted = true;
					getPairedDevicesInProgress = false;
					loadingDevices = false;
					await getPairedDevices(device, { forceRefresh: false, skipIfHasData: false });
					return;
				}
			}

			const resWithResponse = res as Record<string, any>;
			if (resWithResponse?.response?.error || !res?.isok) {
				listError = extractErrorMessage(res);

				if (isPermanentError(res)) {
					getPairedDevicesBlocked = true;
					const fallbackDevices = buildFallbackPairedDevices(deviceID);
					if (fallbackDevices.length) {
						currentControlInputs = cloneInputsArray(fallbackDevices);
						applyPairedDevices(fallbackDevices);
					} else {
						currentControlInputs = [];
						clearPairedDevices();
					}
					return;
				}

				if (getPairedDevicesAttempts < MAX_GET_PAIRED_DEVICES_ATTEMPTS) {
					getPairedDevicesInProgress = false;
					loadingDevices = false;
					getPairedDevicesAttempts -= 1;
					await getPairedDevices(device, { forceRefresh: false, skipIfHasData: false });
					return;
				}
			}

			if (getPairedDevicesAttempts >= MAX_GET_PAIRED_DEVICES_ATTEMPTS) {
				getPairedDevicesBlocked = true;
			}

			const fallbackDevices = buildFallbackPairedDevices(deviceID);
			if (fallbackDevices.length) {
				currentControlInputs = cloneInputsArray(fallbackDevices);
				applyPairedDevices(fallbackDevices);
			} else {
				currentControlInputs = [];
				clearPairedDevices();
			}
		} catch (error) {
			listError = extractErrorMessage(error);

			if (isPermanentError(error)) {
				getPairedDevicesBlocked = true;
				const fallbackDevices = buildFallbackPairedDevices(deviceID);
				if (fallbackDevices.length) {
					currentControlInputs = cloneInputsArray(fallbackDevices);
					applyPairedDevices(fallbackDevices);
				} else {
					currentControlInputs = [];
					clearPairedDevices();
				}
				return;
			}

			if (getPairedDevicesAttempts < MAX_GET_PAIRED_DEVICES_ATTEMPTS) {
				getPairedDevicesInProgress = false;
				loadingDevices = false;
				getPairedDevicesAttempts -= 1;
				await getPairedDevices(device, { forceRefresh: false, skipIfHasData: false });
				return;
			}

			if (getPairedDevicesAttempts >= MAX_GET_PAIRED_DEVICES_ATTEMPTS) {
				getPairedDevicesBlocked = true;
			}

			console.error('[BTHome] Error fetching paired devices:', error);
			const fallbackDevices = buildFallbackPairedDevices(deviceID);
			if (fallbackDevices.length) {
				currentControlInputs = cloneInputsArray(fallbackDevices);
				applyPairedDevices(fallbackDevices);
			} else {
				currentControlInputs = [];
				clearPairedDevices();
			}
		} finally {
			loadingDevices = false;
			getPairedDevicesInProgress = false;
		}
	};

	/**
	 * Builds a human‑readable label for a paired BTHome remote.
	 *
	 * Resolution order:
	 * 1. If the physical device is present in the `devices` store, use its `name`.
	 * 2. Otherwise use the remote settings `name` (if any).
	 * 3. Otherwise use the BTHome model `name` or, as a fallback, a capitalized `deviceType`.
	 * 4. Finally, fall back to a generic "BTHome device ({id})" label.
	 *
	 * When an address is available, it is appended to the label as "<name> - <addr>".
	 */
	/**
	 * Parses an output component ID string into an OutputSelection object.
	 *
	 * @param {string | null | undefined} outputId - Output component ID (e.g., "cover:0", "rgbcct:1")
	 * @returns {OutputSelection} Object with id and name properties
	 *
	 * @example
	 * parseOutputSelection("cover:0")  Returns { id: "0", name: "cover:0" }
	 * parseOutputSelection(null) Returns { id: "<channel>", name: mainDeviceCategory }
	 */
	const parseOutputSelection = (outputId?: string | null): OutputSelection => {
		if (!outputId || typeof outputId !== 'string') {
			const [fallbackType, fallbackChannel = '0'] = (mainDeviceCategory || '').split(':');

			return {
				id: fallbackChannel,
				name: mainDeviceCategory || `${fallbackType}:${fallbackChannel}`,
			};
		}
		const [type, channel = '0'] = outputId.split(':');
		return {
			id: channel,
			name: `${type}:${channel}`,
		};
	};

	/**
	 * Builds the inputs array from cached remote configurations.
	 * Merges current control inputs with cached configs to preserve mappings.
	 *
	 * @returns {Array<Record<string, any>>} Array of input configurations
	 */
	const buildInputsFromRemoteConfigs = () => {
		if (currentControlInputs && currentControlInputs.length > 0) {
			const inputs = cloneInputsArray(currentControlInputs);

			inputs.forEach((entry) => {
				if (!entry) {
					return;
				}
				const remoteId = Object.keys(entry)[0];
				if (!remoteId) {
					return;
				}

				const currentConfig = entry[remoteId];
				const cachedConfig = remoteInputConfigsById[remoteId];

				if (
					Object.keys(currentConfig || {}).length === 0 &&
					cachedConfig &&
					Object.keys(cachedConfig).length > 0
				) {
					entry[remoteId] = cloneValue(cachedConfig);
				}
			});

			return inputs;
		}

		return Object.entries(remoteInputConfigsById).map(([remoteId, config]) => ({
			[remoteId]: cloneValue(config),
		}));
	};

	/**
	 * Filters out metadata fields from a remote device configuration, keeping only obj_id mappings.
	 * Obj_ids must be in the format "number:number" (e.g., "58:0", "60:0").
	 *
	 * @param {Record<string, any>} config - The remote device configuration
	 * @returns {Record<string, any>} Configuration with only obj_id mappings
	 */
	const filterObjIdMappings = (config: Record<string, any>): Record<string, any> => {
		if (!config || typeof config !== 'object') {
			return {};
		}

		const objIdRegex = /^\d+:\d+$/;
		const filtered: Record<string, any> = {};

		Object.entries(config).forEach(([key, value]) => {
			if (objIdRegex.test(key)) {
				filtered[key] = cloneValue(value);
			}
		});

		return filtered;
	};

	/**
	 * Builds an inputs array without the given remote, preserving the other entries.
	 * Filters out metadata fields (id, addr, name, key, meta) from preserved entries.
	 *
	 * @param {Array<Record<string, any>>} existing - Existing input configurations
	 * @param {string} remoteKey - Remote key to remove (e.g., "bthomecontrol:200")
	 * @returns {Array<Record<string, any>>} Inputs array without the removed remote
	 */
	const buildInputsWithoutRemote = (
		existing: Array<Record<string, any>>,
		remoteKey: string,
	): Array<Record<string, any>> => {
		if (!Array.isArray(existing)) {
			return [];
		}

		const REMOTE_INPUT_REGEX = /^(bthomedevice|bthomecontrol):2\d{2}$/;

		return existing
			.map((entry) => {
				if (!entry || typeof entry !== 'object') {
					return null;
				}
				if (Object.prototype.hasOwnProperty.call(entry, remoteKey)) {
					return null;
				}

				const preservedEntry: Record<string, any> = {};
				for (const [key, value] of Object.entries(entry)) {
					if (REMOTE_INPUT_REGEX.test(key)) {
						const filteredValue = filterObjIdMappings(value);
						if (Object.keys(filteredValue).length > 0) {
							preservedEntry[key] = filteredValue;
						}
					}
				}

				return Object.keys(preservedEntry).length > 0 ? preservedEntry : null;
			})
			.filter((entry): entry is Record<string, any> => Boolean(entry));
	};

	/**
	 * Builds an inputs array without all remotes for a specific output component.
	 *
	 * @param {Array<Record<string, any>>} existing - Existing input configurations
	 * @param {string} targetOutput - Output component to filter by (e.g., "rgbcct:0")
	 * @returns {Array<Record<string, any>>} Inputs array without remotes for the target output
	 */
	const buildInputsWithoutOutputRemotes = (
		existing: Array<Record<string, any>>,
		targetOutput: string,
	): Array<Record<string, any>> => {
		if (!Array.isArray(existing)) {
			return [];
		}

		const targetOutputName = parseOutputSelection(targetOutput).name;

		return existing
			.map((entry) => {
				if (!entry || typeof entry !== 'object') {
					return null;
				}

				const entryKeys = Object.keys(entry);
				const filteredEntry: Record<string, any> = {};

				for (const remoteKey of entryKeys) {
					const remoteOutput = remoteOutputById[remoteKey];
					if (remoteOutput) {
						const remoteOutputName = parseOutputSelection(remoteOutput).name;
						if (remoteOutputName !== targetOutputName) {
							filteredEntry[remoteKey] = cloneValue(entry[remoteKey]);
						}
					} else {
						filteredEntry[remoteKey] = cloneValue(entry[remoteKey]);
					}
				}

				return Object.keys(filteredEntry).length > 0 ? filteredEntry : null;
			})
			.filter((entry): entry is Record<string, any> => Boolean(entry));
	};

	/**
	 * Finds a remote ID by its Bluetooth address.
	 *
	 * @param {string | null | undefined} addr - Bluetooth address to search for
	 * @returns {string | null} Remote ID if found, null otherwise
	 */
	const findRemoteIdByAddr = (addr?: string | null) => {
		if (!addr) {
			return null;
		}
		const remoteId = Object.keys(remoteInputConfigsById).find(
			(candidateId) => remoteAddrById[candidateId] === addr && remoteInputConfigsById[candidateId],
		);
		return remoteId || null;
	};

	/**
	 * Resolves a remote ID to its canonical form, checking cached configs and address mappings.
	 *
	 * @param {string | undefined | null} remote - Remote ID to resolve
	 * @returns {string | undefined | null} Resolved remote ID
	 */
	const resolveRemoteId = (remote: string | undefined | null) => {
		if (!remote) {
			return remote;
		}
		if (remoteInputConfigsById[remote]) {
			return remote;
		}
		const addr = remoteAddrById[remote];
		if (addr) {
			const matchingRemoteId = findRemoteIdByAddr(addr);
			if (matchingRemoteId) {
				return matchingRemoteId;
			}
		}
		return remote;
	};

	const deriveRemoteChannelData = (
		remoteId: string,
		remoteConfig: Record<string, any>,
		context?: {
			settingsMap?: Record<string, any>;
			statusMap?: Record<string, any>;
			targetId?: string;
			remoteAddr?: string | null;
		},
	) => {
		const settingsMap =
			context?.settingsMap ?? ((readDeviceSettings(deviceID) ?? {}) as Record<string, any>);
		const statusMap =
			context?.statusMap ?? ((readDeviceStatus(deviceID) ?? {}) as Record<string, any>);

		const candidateIds =
			context?.targetId && context.targetId !== remoteId
				? [context.targetId, remoteId]
				: [remoteId];

		let selectedId = remoteId;
		let remoteSettings: Record<string, any> = {};

		for (const candidate of candidateIds) {
			if (settingsMap?.[candidate]) {
				selectedId = candidate;
				remoteSettings = settingsMap[candidate] as Record<string, any>;
				break;
			}
		}

		if (Object.keys(remoteSettings).length === 0 && settingsMap?.[remoteId]) {
			remoteSettings = settingsMap[remoteId] as Record<string, any>;
			selectedId = remoteId;
		}

		const remoteStatusRecord =
			(statusMap?.[selectedId] as Record<string, any>) ??
			(statusMap?.[remoteId] as Record<string, any>) ??
			{};
		const remoteStatus = remoteStatusRecord as VirtualComponentInterface['status'];
		const remoteModel = getBthomeDeviceModel(remoteStatus);
		const remoteAddr =
			context?.remoteAddr ??
			remoteSettings?.addr ??
			remoteStatusRecord?._attrs?.addr ??
			remoteStatusRecord?.addr ??
			remoteConfig?.addr ??
			remoteConfig?._attrs?.addr;
		const isRemoteModel =
			remoteModel && ['remote_control', 'button'].includes(remoteModel.deviceType || '');

		const sensors = Object.entries(settingsMap).filter(
			([key, config]) =>
				/^bthomesensor:2\d{2}$/.test(key) &&
				config &&
				(config as Record<string, any>)?.addr === remoteAddr &&
				typeof (config as Record<string, any>)?.obj_id === 'number' &&
				typeof (config as Record<string, any>)?.idx === 'number' &&
				REMOTE_EVENT_OBJ_IDS.has((config as Record<string, any>)?.obj_id),
		) as Array<[string, Record<string, any>]>;
		const events: Record<string, any> = {};
		const labels: Record<string, string> = {};

		sensors.forEach(([, sensorConfig]) => {
			const objId = sensorConfig?.obj_id;
			const idx = sensorConfig?.idx;

			if (typeof objId !== 'number' || typeof idx !== 'number') {
				return;
			}

			const channelKey = `${objId}:${idx}`;
			const existingChannelConfig = remoteConfig?.[channelKey];

			events[channelKey] = existingChannelConfig ?? {};

			const derivedLabel = getChannelLabel(objId, idx);
			const label =
				derivedLabel ||
				sensorConfig?.name ||
				bthomeObjectTitle(sensorConfig) ||
				get(format)('remote_channel_label', {
					values: { channel: `${idx}` },
					default: `Channel ${idx}`,
				});

			labels[channelKey] = label;
		});

		if (Object.keys(events).length === 0 && remoteConfig) {
			Object.entries(remoteConfig).forEach(([channelKey, channelEvents]) => {
				if (!channelEvents) {
					return;
				}

				events[channelKey] = channelEvents as Record<string, any>;

				if (!labels[channelKey]) {
					const [objIdStr, idxStr] = channelKey.split(':');
					const objId = Number(objIdStr);
					const idx = Number(idxStr);
					let derivedLabel = null;
					if (Number.isFinite(objId)) {
						const labelIdx = Number.isFinite(idx) ? idx : 0;
						derivedLabel = getChannelLabel(objId, labelIdx);
					}
					labels[channelKey] =
						derivedLabel ||
						get(format)('remote_channel_label', {
							values: { channel: channelKey },
							default: `Channel ${channelKey}`,
						});
				}
			});
		}

		const hasEvents = Object.keys(events).length > 0;
		const effectiveIsRemote = isRemoteModel || sensors.length > 0 || hasEvents;

		return { events, labels, remoteAddr, isRemote: effectiveIsRemote };
	};

	const applyPairedDevices = (details: Array<Record<string, any>>, output?: string) => {
		const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
		const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;
		const controlByAddr = new Map<string, string>();
		const controlOutputById: Record<string, string> = {};
		remoteAddrById = {};
		controlIdByAddr = {};
		remoteInputConfigsById = {};
		remoteOutputById = {};

		const normalizedDetails = Array.isArray(details) ? details.slice() : [];
		const existingKeys = new Set(
			normalizedDetails.map((entry) => Object.keys(entry)?.[0]).filter(Boolean),
		);

		Object.entries(settingsMap).forEach(([key, config]) => {
			if (/^bthomedevice:2\d{2}$/.test(key) && config && !existingKeys.has(key)) {
				normalizedDetails.push({ [key]: config as Record<string, any> });
			}
		});

		currentControlInputs = cloneInputsArray(normalizedDetails);

		Object.entries(settingsMap).forEach(([key, config]) => {
			const configObj = config as Record<string, any>;
			if (/^bthomecontrol:2\d{2}$/.test(key) && configObj?.addr) {
				controlByAddr.set(configObj.addr, key);
			}
			if (/^bthomecontrol:2\d{2}$/.test(key) && configObj?.output) {
				controlOutputById[key] = configObj.output;
			}
		});

		const defaultOutputStr = output || '';

		const controlIds: string[] = [];
		const selectableIds: string[] = [];
		const mappedIds: string[] = [];
		const labels: Record<string, string> = {};
		const channelLabels: Record<string, Record<string, string>> = {};
		const detailEntries: Record<string, Record<string, any>> = {};
		const idByAddr = new Map<string, string>();

		const removeFromArray = (arr: string[], value: string) => {
			const idx = arr.indexOf(value);
			if (idx !== -1) {
				arr.splice(idx, 1);
			}
		};

		const migrateIdData = (fromId: string, toId: string) => {
			if (!fromId || fromId === toId) {
				return;
			}

			if (detailEntries[fromId]) {
				detailEntries[toId] = {
					...(detailEntries[toId] || {}),
					...detailEntries[fromId],
				};
				delete detailEntries[fromId];
			}

			if (labels[fromId] && !labels[toId]) {
				labels[toId] = labels[fromId];
			}
			delete labels[fromId];

			if (channelLabels[fromId]) {
				channelLabels[toId] = {
					...(channelLabels[toId] || {}),
					...channelLabels[fromId],
				};
				delete channelLabels[fromId];
			}

			removeFromArray(selectableIds, fromId);
			removeFromArray(controlIds, fromId);
		};

		const resolveCanonicalId = (
			id: string,
			addr: string | null | undefined,
			isControl: boolean,
		): string => {
			if (!addr) {
				return id;
			}

			const existingId = idByAddr.get(addr);
			if (!existingId) {
				idByAddr.set(addr, id);
				return id;
			}

			if (existingId === id) {
				return id;
			}

			if (isControl && !existingId.startsWith('bthomecontrol')) {
				migrateIdData(existingId, id);
				idByAddr.set(addr, id);
				return id;
			}

			return existingId;
		};

		const registerRemote = (
			id: string,
			events: Record<string, any>,
			derivedLabels: Record<string, string>,
			configSource: Record<string, any>,
			addr: string | null | undefined,
			isControl: boolean,
		): string => {
			if (!id) {
				return id;
			}

			const canonicalId = resolveCanonicalId(id, addr, isControl);

			if (!selectableIds.includes(canonicalId)) {
				selectableIds.push(canonicalId);
			}

			if (addr) {
				remoteAddrById[canonicalId] = addr;
				if (isControl) {
					controlIdByAddr[addr] = canonicalId;
				}
			}

			detailEntries[canonicalId] = {
				...(detailEntries[canonicalId] || {}),
				...events,
			};

			if (isControl || !labels[canonicalId]) {
				labels[canonicalId] = getFallbackRemoteLabel(
					deviceID,
					canonicalId,
					configSource || {},
					addr,
				);
			}

			channelLabels[canonicalId] = {
				...(channelLabels[canonicalId] || {}),
				...derivedLabels,
			};

			if (isControl && !controlIds.includes(canonicalId)) {
				controlIds.push(canonicalId);
			}

			return canonicalId;
		};

		normalizedDetails.forEach((entry) => {
			const remoteId = entry && Object.keys(entry)?.[0];
			if (!remoteId) {
				return;
			}
			const remoteConfig = entry?.[remoteId] || {};
			if (remoteConfig && remoteConfig !== null) {
				remoteInputConfigsById[remoteId] = cloneValue(remoteConfig);
			}
			const remoteSettings = (settingsMap?.[remoteId] || {}) as Record<string, any>;
			const remoteStatusRecord = (statusMap?.[remoteId] || {}) as Record<string, any>;
			const initialAddr =
				remoteSettings?.addr ??
				remoteStatusRecord?._attrs?.addr ??
				remoteStatusRecord?.addr ??
				remoteConfig?.addr ??
				remoteConfig?._attrs?.addr;
			if (initialAddr) {
				remoteAddrById[remoteId] = initialAddr;
			}
			const preferredId = initialAddr ? controlByAddr.get(initialAddr) : undefined;
			const {
				events,
				labels: derivedLabels,
				remoteAddr,
				isRemote,
			} = deriveRemoteChannelData(remoteId, remoteConfig, {
				settingsMap,
				statusMap,
				targetId: preferredId,
				remoteAddr: initialAddr,
			});

			const isFromInputsList = /^bthome(device|control):2\d{2}$/.test(remoteId);

			if (!Object.keys(events).length && !isFromInputsList) {
				return;
			}
			if (!isRemote && !isFromInputsList) {
				return;
			}

			const normalizedId =
				(remoteAddr && controlByAddr.get(remoteAddr)) || preferredId || remoteId;

			let remoteOutput = defaultOutputStr;
			if (/^bthomecontrol:2\d{2}$/.test(normalizedId)) {
				remoteOutput = controlOutputById[normalizedId] || defaultOutputStr;
			} else if (/^bthomedevice:2\d{2}$/.test(remoteId)) {
				const associatedControlId =
					preferredId || (remoteAddr ? controlByAddr.get(remoteAddr) : undefined);
				if (associatedControlId && controlOutputById[associatedControlId]) {
					remoteOutput = controlOutputById[associatedControlId];
				} else {
					remoteOutput = defaultOutputStr;
				}
			}

			let finalCanonicalId: string;
			if (/^bthomecontrol:2\d{2}$/.test(normalizedId)) {
				const controlConfig = (settingsMap?.[normalizedId] || {}) as Record<string, any>;
				finalCanonicalId = registerRemote(
					normalizedId,
					events,
					derivedLabels,
					Object.keys(controlConfig).length ? controlConfig : remoteConfig,
					remoteAddr,
					true,
				);
				remoteOutputById[finalCanonicalId] = remoteOutput;
			} else if (isFromInputsList) {
				finalCanonicalId = registerRemote(
					remoteId,
					events,
					derivedLabels,
					remoteConfig,
					remoteAddr,
					true,
				);
				remoteOutputById[finalCanonicalId] = remoteOutput;
			} else {
				finalCanonicalId = registerRemote(
					remoteId,
					events,
					derivedLabels,
					remoteConfig,
					remoteAddr,
					false,
				);
				remoteOutputById[finalCanonicalId] = remoteOutput;
			}
		});

		if (!selectableIds.length) {
			clearPairedDevices();
			return;
		}

		mappingCountById = {};
		mappingCountByIdAndChannel = {};
		pairedDeviceDetails = selectableIds.map((id) => ({ [id]: detailEntries[id] }));
		pairedDevices = selectableIds;
		pairedDeviceLabels = labels;
		pairedDeviceChannelLabels = channelLabels;
		const getChannelIndexFromEventKey = (eventKey: string): number | null => {
			if (!eventKey || typeof eventKey !== 'string') {
				return null;
			}
			const parts = eventKey.split('@');
			if (parts.length < 2) {
				return 0;
			}
			const parsed = Number(parts[1]);
			return Number.isFinite(parsed) ? parsed : null;
		};

		const incrementChannelMappingCount = (remoteId: string, channelIdx: number | null) => {
			if (!remoteId) {
				return;
			}
			const idx = channelIdx ?? 0;
			if (!mappingCountByIdAndChannel[remoteId]) {
				mappingCountByIdAndChannel[remoteId] = {};
			}
			const channelMap = mappingCountByIdAndChannel[remoteId];
			channelMap[idx] = (channelMap[idx] ?? 0) + 1;
		};

		const countMappingsByChannel = (remoteId: string, config: Record<string, any>): number => {
			if (!config) {
				return 0;
			}

			let total = 0;
			Object.entries(config).forEach(([channelKey, channelEvents]) => {
				if (!channelEvents || typeof channelEvents !== 'object') {
					return;
				}

				const eventsRecord = channelEvents as Record<string, any>;
				Object.entries(eventsRecord).forEach(([eventKey, eventCfg]) => {
					const action = (eventCfg as Record<string, any>)?.action;
					if (typeof action === 'string' && action) {
						total += 1;
						const channelIdx = getChannelIndexFromEventKey(eventKey);
						incrementChannelMappingCount(remoteId, channelIdx);
					}
				});
			});

			return total;
		};

		Object.entries(remoteInputConfigsById).forEach(([remoteId, config]) => {
			const mappingCount = countMappingsByChannel(remoteId, config || ({} as Record<string, any>));
			mappingCountById[remoteId] = mappingCount;
			const addr = remoteAddrById?.[remoteId];
			if (addr) {
				const canonicalId = controlIdByAddr[addr];
				if (canonicalId) {
					mappingCountById[canonicalId] = mappingCount;
				}
			}
		});
		selectableIds.forEach((id) => {
			const detailConfig = detailEntries[id];
			if (detailConfig) {
				const mappingCount = countMappings(detailConfig);
				if (mappingCount > 0) {
					mappingCountById[id] = Math.max(mappingCountById[id] ?? 0, mappingCount);
				}
			}
		});
		selectableIds.forEach((id) => {
			let mappingCount = mappingCountById[id] ?? 0;
			const addr = remoteAddrById?.[id];
			if (addr) {
				const canonicalId = controlIdByAddr[addr];
				if (canonicalId && canonicalId !== id) {
					const canonicalMappingCount = mappingCountById[canonicalId] ?? 0;
					mappingCount = Math.max(mappingCount, canonicalMappingCount);
				}
			}

			if (mappingCount > 0 && !mappedIds.includes(id)) {
				mappedIds.push(id);
			}
		});
		mappedRemoteIds = mappedIds;
		btHomeRemoteIcon.setState(deviceID, mappedRemoteIds.length > 0);
	};

	/**
	 * Clears all paired device state including labels, mappings, and cached configurations.
	 */
	const clearPairedDevices = () => {
		pairedDeviceDetails = [];
		pairedDevices = [];
		mappedRemoteIds = [];
		pairedDeviceLabels = {};
		pairedDeviceChannelLabels = {};
		remoteAddrById = {};
		controlIdByAddr = {};
		mappingCountById = {};
		currentControlInputs = [];
		remoteInputConfigsById = {};
		btHomeRemoteIcon.setState(deviceID, false);
	};

	/**
	 * Gets the configuration for a remote from cached configs or paired device details.
	 *
	 * @param {string | null | undefined} id - Remote ID
	 * @returns {Record<string, any> | undefined} Remote configuration or undefined
	 */
	const getDetailEntryConfig = (id: string | null | undefined) => {
		if (!id) {
			return undefined;
		}
		if (remoteInputConfigsById[id]) {
			return remoteInputConfigsById[id];
		}
		const entry = pairedDeviceDetails.find((detail) => detail && id in detail);
		return entry?.[id];
	};

	const openRemoteEditor = (
		output: OutputSelection | undefined | null,
		remote: string | undefined,
		channelLabels?: Record<string, string>,
		deviceType?: string,
	) => {
		const resolvedRemote = resolveRemoteId(remote);
		if (!output || !resolvedRemote) {
			return;
		}

		const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
		const numericRemoteId = Number(resolvedRemote?.split(':')?.[1]);
		const suffixControlId =
			Number.isFinite(numericRemoteId) && numericRemoteId >= 0
				? `bthomecontrol:${numericRemoteId}`
				: null;
		const remoteSettingsEntry = (settingsMap?.[resolvedRemote] as Record<string, any>) || {};
		const remoteAddr =
			(remoteAddrById[resolvedRemote] as string | undefined) ??
			(remoteSettingsEntry?.addr as string | undefined) ??
			(remoteSettingsEntry?._attrs?.addr as string | undefined) ??
			null;
		const controlIdFromAddr = (remoteAddr && controlIdByAddr[remoteAddr]) || null;
		const candidateControlId = controlIdFromAddr || suffixControlId;

		const configCandidates = [
			remoteInputConfigsById[resolvedRemote],
			getDetailEntryConfig(resolvedRemote),
			getDetailEntryConfig(candidateControlId),
			candidateControlId ? (settingsMap?.[candidateControlId] as Record<string, any>) : undefined,
			settingsMap?.[resolvedRemote] as Record<string, any>,
		];
		let config: Record<string, any> = {};
		for (const candidate of configCandidates) {
			if (hasConfigEntries(candidate)) {
				config = cloneConfig(candidate);
				break;
			}
		}

		const remoteChannelLabels =
			channelLabels ||
			pairedDeviceChannelLabels?.[resolvedRemote] ||
			(remote ? pairedDeviceChannelLabels?.[remote] : {}) ||
			{};
		const controlChannelLabels =
			(candidateControlId ? pairedDeviceChannelLabels?.[candidateControlId] : null) || {};
		const suffixChannelLabels =
			suffixControlId && suffixControlId !== candidateControlId
				? pairedDeviceChannelLabels?.[suffixControlId] || {}
				: {};
		const derivedChannelLabels = {
			...controlChannelLabels,
			...suffixChannelLabels,
			...remoteChannelLabels,
		};
		const existingInputs = buildInputsFromRemoteConfigs();

		const remoteExistsInSettings =
			ensuredRemoteIds.has(resolvedRemote) ||
			(remoteSettingsEntry && Object.keys(remoteSettingsEntry || {}).length > 0);

		selectedExistingRemote = {
			output,
			remoteId: resolvedRemote,
			config,
			channelLabels: derivedChannelLabels,
			existingInputs,
			remoteExistsInSettings,
			hasMappedRemotes: mappedRemoteIds.length > 0,
			deviceType: deviceType,
		};
		showCreateRemoteModal = true;
	};

	const ensureRemoteComponentExists = async (
		output: OutputSelection | undefined | null,
		remote: string | undefined,
	) => {
		const resolvedRemote = resolveRemoteId(remote);
		const remoteKey = resolvedRemote || remote;
		if (!remoteKey) {
			return false;
		}

		if (!/^bthomedevice:2\d{2}$/.test(remoteKey)) {
			ensuredRemoteIds.add(remoteKey);
			return true;
		}

		const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
		if (settingsMap?.[remoteKey]) {
			ensuredRemoteIds.add(remoteKey);
			return true;
		}

		const payloadOutput =
			output?.name || output?.id || defaultOutput?.name || `${mainDeviceCategory}:0`;

		const res: SettingRequest = await BtHomeController.getInstance().create(device, {
			output: payloadOutput,
			inputs: [
				{
					[remoteKey]: {},
				},
			],
		});

		if (res?.isok) {
			ensuredRemoteIds.add(remoteKey);
			await getPairedDevices(device, {});
			return true;
		}

		const rawError =
			res?.errors?.message ?? res?.error ?? res?.data?.error ?? 'Failed to pair remote';
		throw new Error(typeof rawError === 'string' ? rawError : JSON.stringify(rawError));
	};

	const handleUseExistingRemote = async (event) => {
		const { output, remote, channelLabels } = event.detail || {};
		try {
			const ensured = await ensureRemoteComponentExists(output, remote);
			if (!ensured) {
				toast.open({
					type: 'danger',
					title: $format('toast_remote_creation_failed', {
						default: 'Failed to prepare remote',
					}),
				});
				return;
			}
		} catch (error) {
			toast.open({
				type: 'danger',
				title: $format('toast_remote_creation_failed', {
					default: 'Failed to prepare remote',
				}),
				text: error instanceof Error ? error.message : String(error),
			});
			return;
		}

		openRemoteEditor(output, remote, channelLabels);
	};

	const handleUseDiscoveredDevice = async (event) => {
		const { output, device: discoveredDevice } = event.detail || {};
		if (!output || !discoveredDevice?.addr) {
			return;
		}

		try {
			const outputId = output.name || output.id || 'rgbcct:0';
			const payload = {
				output: outputId,
				inputs: [
					{
						[`bthomedevice:${discoveredDevice.addr.replace(/:/g, '')}`]: {},
					},
				],
			};

			const res: SettingRequest = await BtHomeController.getInstance().create(device, payload);
			if (res?.isok) {
				await getPairedDevices(device, {});
				toast.open({
					type: 'success',
					title: $format('toast_remote_created_successfully', {
						default: 'Remote created successfully.',
					}),
				});
			} else {
				const errorMsg =
					res?.errors?.message ?? res?.error ?? res?.data?.error ?? 'Failed to create remote';
				toast.open({
					type: 'danger',
					title: $format('toast_remote_creation_failed', {
						default: 'Failed to create remote',
					}),
					text: errorMsg,
				});
			}
		} catch (error) {
			toast.open({
				type: 'danger',
				title: $format('toast_remote_creation_failed', {
					default: 'Failed to create remote',
				}),
				text: error instanceof Error ? error.message : String(error),
			});
		}
	};

	/**
	 * Finds the remote ID for a component group by its address.
	 *
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {string | null} Remote ID or group key
	 */
	const findRemoteIdForGroup = (group: ComponentsGroupInterface) => {
		const addr = group?.config?.addr;
		return findRemoteIdByAddr(addr) || group?.key;
	};

	/**
	 * Handles click on a paired remote to open the editor modal.
	 *
	 * @param {ComponentsGroupInterface} group - Component group for the remote
	 * @param {string | undefined} deviceType - Optional device type
	 */
	const handlePairedRemoteClick = (group: ComponentsGroupInterface, deviceType?: string) => {
		const remoteId = findRemoteIdForGroup(group);
		openRemoteEditor(
			defaultOutput,
			remoteId,
			pairedDeviceChannelLabels?.[group.key] || {},
			deviceType,
		);
	};

	/**
	 * Gets the canonical remote ID for a group, preferring bthomecontrol IDs over bthomedevice.
	 *
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {string} Canonical remote ID
	 */
	const getCanonicalRemoteId = (group: ComponentsGroupInterface) => {
		const addr = group?.config?.addr;
		return (addr && controlIdByAddr[addr]) || group?.key;
	};

	/**
	 * Gets the total number of action mappings for a component group.
	 * The count shown in the UI should represent the **total number of
	 * configured actions across all channels** for this remote (left/right
	 * buttons and scroll up/down), not only for the current device channel.
	 *
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {number} Total number of mappings for this remote
	 */
	const getGroupMappingCount = (group: ComponentsGroupInterface) => {
		const canonicalId = getCanonicalRemoteId(group);

		if (canonicalId && typeof mappingCountById[canonicalId] === 'number') {
			return mappingCountById[canonicalId];
		}

		const fallbackId = findRemoteIdForGroup(group);
		if (fallbackId && typeof mappingCountById[fallbackId] === 'number') {
			return mappingCountById[fallbackId] ?? 0;
		}

		return 0;
	};

	/**
	 * Unpairs a single remote by removing it from inputs and updating BTHomeControl.
	 * Uses enumerate to get the component ID, matching the behavior when editing mappings.
	 *
	 * @param {ComponentsGroupInterface} group - Component group representing the remote
	 */
	const handleUnpairRemote = async (group: ComponentsGroupInterface, index: number) => {
		const canonicalId = getCanonicalRemoteId(group);
		if (!canonicalId) {
			return;
		}

		try {
			loadingDelete[index] = true;

			const currentOutput = defaultOutput?.name || mainDeviceCategory;
			const componentId =
				enumerateSupported === false ? 200 : await getComponentIdFromEnumerate(currentOutput);

			const updatedInputs = buildInputsWithoutRemote(currentControlInputs, canonicalId);

			const payload: Record<string, any> = {
				id: componentId,
				output: defaultOutput?.name || mainDeviceCategory,
				inputs: cloneInputsArray(updatedInputs),
			};

			const res: SettingRequest = await gen2Request(device, 'BTHomeControl.Update', payload);
			onSettingsResponse(res, device);
			if (res?.isok) {
				await getPairedDevices(device, { forceRefresh: true });
			} else {
				const rawError =
					res?.errors?.message ??
					res?.data?.error ??
					(res?.error !== undefined ? res.error : 'Unknown error');
				console.error('Failed to unpair remote', rawError);
			}
		} catch (error) {
			console.error('Failed to unpair remote', error);
		} finally {
			loadingDelete[index] = false;
		}
	};

	/**
	 * Checks if a device with the given address already has action mappings configured.
	 *
	 * @param {string} addr - Bluetooth address to check
	 * @returns {boolean} True if device has mappings
	 */
	const isDeviceAlreadyMapped = (addr: string): boolean => {
		if (!addr) {
			return false;
		}

		const remoteId = Object.keys(remoteAddrById).find((id) => remoteAddrById[id] === addr);
		if (!remoteId) {
			return false;
		}

		const mappingCount = mappingCountById[remoteId] ?? 0;
		const canonicalId = controlIdByAddr[addr];
		let canonicalMappingCount = 0;
		if (canonicalId) {
			canonicalMappingCount = mappingCountById[canonicalId] ?? 0;
		}

		return mappingCount > 0 || canonicalMappingCount > 0;
	};

	/**
	 * Returns whether a mapped group represents a supported *remote control* device.
	 *
	 * Only BTHome models with `deviceType === 'remote_control'` are treated as supported.
	 * Button‑only BTHome devices return `false`, which is used to disable the "Edit mappings" CTA.
	 */
	const isRemoteControlGroup = (group: ComponentsGroupInterface): boolean => {
		if (!group) {
			return false;
		}

		const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
		const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;

		const remoteId = group.key;
		const statusEntry = statusMap?.[remoteId] as Record<string, any> | undefined;
		const settingsEntry = settingsMap?.[remoteId] as Record<string, any> | undefined;

		const deviceLabel = pairedDeviceLabels?.[remoteId];
		if (deviceLabel && /remote/i.test(deviceLabel)) {
			return true;
		}

		if (settingsEntry?.name && /remote/i.test(settingsEntry.name)) {
			return true;
		}

		const addr =
			settingsEntry?.addr ?? statusEntry?._attrs?.addr ?? statusEntry?.addr ?? group.config?.addr;
		if (addr) {
			const devicesMap = get(devices);
			const remoteDevice = devicesMap?.[macToID(addr)] as Record<string, any> | undefined;
			if (remoteDevice?.name && /remote/i.test(remoteDevice.name)) {
				return true;
			}
			if (remoteDevice?.type && /SBRC/i.test(remoteDevice.type)) {
				return true;
			}
		}

		const statusCandidate =
			statusEntry ?? (settingsEntry?._attrs as Record<string, any> | undefined) ?? settingsEntry;
		if (!statusCandidate) {
			return false;
		}

		const model = getBthomeDeviceModel(statusCandidate as any);
		return model?.deviceType === 'remote_control';
	};

	/**
	 * Removes discovered devices that already have mappings from the discovery list.
	 * Refreshes paired devices if any were removed.
	 */
	const removeMappedDiscoveredDevices = () => {
		const discoveredDevices = $btHomeDiscoveredDevices?.[deviceID]?.discoveredDevices || [];
		if (discoveredDevices.length === 0) {
			return;
		}

		let shouldRefresh = false;
		discoveredDevices.forEach((discoveredDevice) => {
			if (!discoveredDevice?.addr) {
				return;
			}

			if (isDeviceAlreadyMapped(discoveredDevice.addr)) {
				btHomeDiscoveredDevices.removeDeviceByAddr(deviceID, discoveredDevice.addr);
				shouldRefresh = true;
			}
		});

		if (shouldRefresh && pairedDevices.length === 0) {
			getPairedDevices(device, {});
		}
	};

	/**
	 * Closes the remote editor modal and clears selected remote state.
	 */
	const closeCreateRemoteModal = () => {
		showCreateRemoteModal = false;
		selectedExistingRemote = null;
	};

	/**
	 * Handles successful remote save by refreshing paired devices and showing success toast.
	 */
	const handleRemoteSaved = async () => {
		await getPairedDevices(device, { forceRefresh: true });
		toast.open({
			type: 'success',
			title: $format('toast_remote_updated_successfully', {
				default: 'Remote updated successfully.',
			}),
		});
		closeCreateRemoteModal();
	};

	onMount(async () => {
		btHomeRemoteIcon.setState(deviceID, false);
		if (supportsBTHomeRemoteControls) {
			await getPairedDevices(device, { forceRefresh: true });
		}
	});

	/**
	 * Unpairs all BTHome remote controls for the current output component.
	 * Dynamically retrieves the component ID via enumerate, falling back to 200.
	 */
	const handleUnpairRemotes = async () => {
		try {
			loading = true;
			const currentOutput = defaultOutput?.name || mainDeviceCategory;
			const componentId =
				enumerateSupported === false ? 200 : await getComponentIdFromEnumerate(currentOutput);

			const updatedInputs = buildInputsWithoutOutputRemotes(currentControlInputs, currentOutput);

			const payload: Record<string, any> = {
				id: componentId,
				output: currentOutput,
				inputs: cloneInputsArray(updatedInputs),
			};

			const res: SettingRequest = await gen2Request(device, 'BTHomeControl.Update', payload);

			if (res?.isok) {
				btHomeDiscoveredDevices.clearDevices(deviceID);
			} else {
				const rawError =
					res?.errors?.message ??
					res?.data?.error ??
					(res?.error !== undefined ? res.error : 'Unknown error');
				throw new Error('Failed to unpair devices' + JSON.stringify(rawError));
			}
		} catch (error) {
			console.error('Failed to delete devices!', error);
		} finally {
			loading = false;
			await getPairedDevices(device, { forceRefresh: true });
		}
	};

	$: if (
		supportsBTHomeRemoteControls &&
		clearedDevices &&
		!getPairedDevicesBlocked &&
		pairedDevices.length === 0
	) {
		getPairedDevices(device, {});
		btHomeDiscoveredDevices.stopDiscovery(deviceID);
	}

	$: if ($btHomeDiscoveredDevices?.[deviceID]?.discoveredDevices?.length > 0) {
		removeMappedDiscoveredDevices();
	}

	let previousRunningDiscovery = false;
	$: {
		const currentlyRunning = $btHomeDiscoveredDevices?.[deviceID]?.runningDiscovery;
		const hasDevices = $btHomeDiscoveredDevices?.[deviceID]?.discoveredDevices?.length > 0;

		if (
			previousRunningDiscovery &&
			!currentlyRunning &&
			hasDevices &&
			!getPairedDevicesBlocked &&
			pairedDevices.length === 0
		) {
			setTimeout(() => {
				if (!getPairedDevicesBlocked && pairedDevices.length === 0) {
					getPairedDevices(device, {}).then(() => {
						btHomeDiscoveredDevices.clearDevices(deviceID);
					});
				}
			}, 2000);
		}

		previousRunningDiscovery = currentlyRunning || false;
	}

	/**
	 * Gets the avatar configuration for a device card.
	 *
	 * @param {any} item - Device item
	 * @returns {Object} Avatar configuration with image path
	 */
	const getDeviceAvatar = (item) => {
		if (item && typeof item !== 'string' && item?.type) {
			return { image: `images/device_images/${item.type}.png` };
		}
		return { image: 'images/device_images/unknown-device.svg' };
	};

	/**
	 * Gets the subtitle text for a remote device card (address and ID).
	 * Shows MAC address similar to when device is not in current account.
	 * Can determine if device is Zigbee from _attrs.model_id, but shows MAC address as requested.
	 *
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {string} Subtitle text
	 */
	const getRemoteSubtitle = (group) => {
		if (!group) {
			return '';
		}
		const addr = group.config?.addr;
		const remoteId = group.config?.id ?? group.key?.split(':')?.[1];

		if (addr && remoteId) {
			return `${addr} (${remoteId})`;
		}
		return addr || (remoteId ? `ID ${remoteId}` : '');
	};

	/**
	 * Gets the title text for a device card.
	 *
	 * @param {any} item - Device item
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {string} Title text
	 */
	const getDeviceTitle = (item, group) => {
		if (item && typeof item !== 'string') {
			return (
				item?.name ||
				item?.type ||
				$format('title_unknown_ble_device', {
					default: 'Unknown BLE Device',
				})
			);
		}

		const remoteId = group?.key;
		if (remoteId) {
			return (
				pairedDeviceLabels?.[remoteId] ||
				getFallbackRemoteLabel(deviceID, remoteId, group?.config || {})
			);
		}

		return $format('title_unknown_ble_device', {
			default: 'Unknown BLE Device',
		});
	};

	/**
	 * Gets the subtitle text for a device card (MAC address and room name for remotes).
	 *
	 * @param {any} item - Device item
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {string} Subtitle text
	 */
	const getDeviceSubtitle = (item, group) => {
		if (typeof item === 'string') {
			return item;
		}

		const remoteSubtitle = getRemoteSubtitle(group);
		const roomName =
			item && item !== undefined && $rooms[item?.room_id]?.name
				? $rooms[item?.room_id]?.name
				: null;

		if (remoteSubtitle && roomName) {
			return `${remoteSubtitle} - ${roomName}`;
		}

		if (remoteSubtitle) {
			return remoteSubtitle;
		}

		if (roomName) {
			return roomName;
		}

		return capFirstChar($format('label_hidden', { default: 'Hidden' }));
	};

	/**
	 * Gets the avatar configuration for a device card (icon or image).
	 *
	 * @param {any} item - Device item
	 * @param {ComponentsGroupInterface} group - Component group
	 * @returns {Object} Avatar configuration
	 */
	const getCardAvatar = (item, group) => {
		if (item && typeof item !== 'string') {
			return getDeviceAvatar(item);
		}

		if (group?.config?.icon) {
			return { icon: group.config.icon };
		}

		return { image: 'images/device_images/unknown-device.svg' };
	};

	$: if (pairedDevices.length > 0 && loadingDevices) {
		loadingDevices = false;
	}

	let lastListRefreshTrigger = 0;
	$: listRefreshTrigger = $btHomeDiscoveredDevices?.[deviceID]?.listRefreshTrigger;
	$: learningInProgress = $btHomeDiscoveredDevices?.[deviceID]?.runningDiscovery;
	$: if (
		supportsBTHomeRemoteControls &&
		listRefreshTrigger !== undefined &&
		listRefreshTrigger > 0 &&
		listRefreshTrigger !== lastListRefreshTrigger &&
		!learningInProgress &&
		!getPairedDevicesBlocked &&
		pairedDevices.length === 0
	) {
		lastListRefreshTrigger = listRefreshTrigger;
		getPairedDevices(device, {});
	}

	onDestroy(() => {
		btHomeDiscoveredDevices.stopDiscovery(deviceID);
		getPairedDevicesBlocked = true;
	});
</script>

{#if !btEnable}
	<Alert
		type="warning"
		heading={$format('alert_message_virtual_components_ble_disabled', {
			default: 'Bluetooth is disabled. Enable it in order to use BTHome Components feature.',
		})}
		cta={{
			title: $format('alert_title_virtual_components_go_to_blu_settings', {
				default: 'Go to Bluetooth settings',
			}),
		}}
		on:cta_click={() => navigateToSetting(real_id(device.id), 'bluetooth', 'bluetooth_settings')}
	/>
{:else if !supportsBTHomeRemoteControls}
	<Alert
		type="warning"
		heading={$format('alert_message_bthome_remote_controls_fw_required', {
			default: 'BTHome Remote Controls requires firmware version 1.8.0 or higher.',
		})}
		message={fwVersion
			? $format('alert_message_current_fw_version', {
					values: { version: fwVersion },
					default: `Your current firmware version is ${fwVersion}. Please update to 1.8.0 or higher.`,
				})
			: $format('alert_message_fw_version_unknown', {
					default:
						'Unable to determine firmware version. Please ensure your device firmware is up to date.',
				})}
	/>
{:else if loadingDevices}
	<Loader size="l" class="d-flex justify-content-center mt-s" />
{:else}
	<BTHomeLearning
		{device}
		{pairedDevices}
		{pairedDeviceLabels}
		{pairedDeviceChannelLabels}
		on:learningCompleted={() => {
			listError = null;
			getPairedDevices(device, { forceRefresh: true });
		}}
		on:useExistingRemote={handleUseExistingRemote}
		on:useDiscoveredDevice={handleUseDiscoveredDevice}
	/>
	{#if listError}
		<Alert
			type="warning"
			class="mb-s"
			heading={$format('error_fetching_paired_devices', {
				default: 'Error fetching paired devices',
			})}
		/>
	{/if}
	{#if mappedRemoteIds.length === 0 && !listError}
		<Alert
			type="info"
			heading={$format('no_devices_paired', {
				default: 'No devices paired yet.',
			})}
			message={$format('alert_message_how_to_pair_bt_remote', {
				default:
					'To pair a Bluetooth remote controller, click "Start pairing" above and follow the instructions. Make sure your remote is in pairing mode.',
			})}
		/>
	{:else}
		{@const mappedGroups = groups.filter((group) => {
			const canonicalId = getCanonicalRemoteId(group);
			if (!canonicalId || !mappedRemoteIds.includes(canonicalId)) {
				return false;
			}

			if (!mainDeviceCategory) {
				return false;
			}

			const remoteOutput = remoteOutputById[canonicalId];
			if (!remoteOutput) {
				return false;
			}

			const outputMatch = parseOutputSelection(remoteOutput);
			const currentOutput = parseOutputSelection(mainDeviceCategory);
			return outputMatch.name === currentOutput.name;
		})}
		{#each mappedGroups as group, index}
			<Container class="mb-s" padding="0" key={index}>
				{#if device.channel < 100}
					{@const _device = group?.config?.addr ? $devices[macToID(group.config.addr)] : null}
					<div
						class="d-flex flex-direction-row ml-s mt-s align-items-center justify-content-between gap-s"
					>
						<div class="d-flex align-items-center gap-s flex-wrap mb-s">
							<Text styleType="body-small" class="text-secondary">
								{$format('text_remote_mappings', {
									values: { count: getGroupMappingCount(group) },
									default: `${getGroupMappingCount(group)} mappings`,
								})}
							</Text>
						</div>
						<div class="mb-s mr-s">
							<IconButton
								icon="fas fa-edit"
								variant="primary"
								size="xs"
								disabled={!isRemoteControlGroup(group)}
								on:click={() => handlePairedRemoteClick(group, _device?.type)}
								aria-label={$format('button_edit_mappings', {
									default: 'Edit mappings',
								})}
							/>
							<IconButton
								icon="fas fa-trash"
								variant="danger"
								on:click={() => handleUnpairRemote(group, index)}
								aria-label={$format('button_unpair_devices', {
									default: 'Unpair devices',
								})}
								loading={loadingDelete[index] || false}
							/>
						</div>
					</div>

					<AdvancedCard
						isFocusable={typeof _device !== 'string' && _device !== undefined}
						title={getDeviceTitle(_device, group)}
						subtitle={{
							type: 'secondary',
							bold: false,
							textSmall: true,
							value: getDeviceSubtitle(_device, group),
						}}
						avatar={getCardAvatar(_device, group)}
						class="width-full"
						on:click={() => navigateToDevice(real_id(_device?.id || ''))}
					/>
				{:else}
					{#if !device.shared}
						<Text
							class="link-styles text-small mt-s mb-s ml-xs"
							on:click={() => navigateToSetting(deviceID, 'virtual_components')}
						>
							{$format('text_virtual_component_open_main_device', {
								default: 'Open main device',
							})}
						</Text>
					{/if}
					{#if !group?.items?.length}
						<Alert
							type="info"
							heading={$format('no_virtual_components_title', {
								default: 'There are no virtual components yet',
							})}
						/>
					{/if}
				{/if}
			</Container>
		{/each}
		{#if mappedGroups.length > 0}
			<Button
				class="width-full"
				icon="fas fa-trash"
				variant="danger"
				size="m"
				{loading}
				on:click={() => handleUnpairRemotes()}
				>{$format('button_unpair_devices', { default: 'Unpair devices' })}</Button
			>
		{/if}
	{/if}
{/if}

{#if showCreateRemoteModal && selectedExistingRemote}
	<BTHomeCreateRemoteModal
		{device}
		output={selectedExistingRemote.output}
		remoteId={selectedExistingRemote.remoteId}
		remoteConfig={selectedExistingRemote.config}
		channelLabels={selectedExistingRemote.channelLabels}
		existingInputs={selectedExistingRemote.existingInputs}
		remoteExistsInSettings={selectedExistingRemote.remoteExistsInSettings}
		hasMappedRemotes={selectedExistingRemote.hasMappedRemotes}
		pairedDeviceType={selectedExistingRemote.deviceType}
		showModal={showCreateRemoteModal}
		on:close={closeCreateRemoteModal}
		on:saved={handleRemoteSaved}
	/>
{/if}
